import { columnCharsToPx, defaultColumnPx, generalText, isGeneralFormat } from './parser/format';
import type { CellBorders, CellIcon, DataBar, FontStyle, Sheet, SheetCell, SheetMerge } from './parser';

export const CELL_PADDING = 2;
export const INDENT_STEP = 9;
export const COLUMN_HEADER_HEIGHT = 20;

const LINE_RATIO: Record<string, number> = {
  calibri: 1.2207,
  'calibri light': 1.2207,
  carlito: 1.2207,
  arial: 1.149,
  'arial cyr': 1.149,
  'arial narrow': 1.149,
  'times new roman': 1.149,
  'times new roman cyr': 1.149,
  cambria: 1.172,
  tahoma: 1.207,
  verdana: 1.215,
  'segoe ui': 1.33,
  'courier new': 1.133,
  consolas: 1.17,
  georgia: 1.136,
};

const SERIF_FONTS = /times|cambria|georgia|garamond|serif/i;
const MONO_FONTS = /courier|consolas|mono/i;

export function fontFamily(fontName?: string): string {
  const name = fontName
    ?.replace(/[^\p{L}\p{N} ._-]/gu, '')
    .trim()
    .slice(0, 64);
  const fallback = MONO_FONTS.test(name ?? '')
    ? '"Courier New", monospace'
    : SERIF_FONTS.test(name ?? '')
      ? '"Times New Roman", serif'
      : 'Calibri, Carlito, Arial, sans-serif';
  return name ? `"${name}", ${fallback}` : fallback;
}

export function fontPx(font: FontStyle): number {
  const size = (font.fontSize ?? 11) * (4 / 3);
  return font.vertAlign ? size * 0.75 : size;
}

export function cssFont(font: FontStyle, scale = 1): string {
  return `${font.italic ? 'italic ' : ''}${font.bold ? 700 : 400} ${fontPx(font) * scale}px ${fontFamily(
    font.fontName,
  )}`;
}

export function lineHeight(font: FontStyle): number {
  const ratio = LINE_RATIO[(font.fontName ?? 'calibri').toLowerCase()] ?? 1.2;
  return Math.round((font.fontSize ?? 11) * (4 / 3) * ratio) + 2;
}

let context: CanvasRenderingContext2D | null | undefined;

function measure(text: string, font: FontStyle): number {
  if (context === undefined) {
    try {
      context = document.createElement('canvas').getContext('2d');
    } catch {
      context = null;
    }
  }
  if (!context) {
    return text.length * fontPx(font) * 0.55;
  }
  context.font = cssFont(font);
  return context.measureText(text).width;
}

export function digitWidth(font: FontStyle): number {
  let max = 0;
  for (let digit = 0; digit <= 9; digit++) {
    max = Math.max(max, measure(String(digit), font));
  }
  return Math.max(Math.round(max), 1);
}

function wrapLines(text: string, font: FontStyle, width: number): number {
  if (width <= 0) {
    return 1;
  }
  let lines = 0;
  text.split(/\r?\n/).forEach((paragraph) => {
    let line = '';
    paragraph.split(/(?<=\s)/).forEach((word) => {
      const candidate = line + word;
      if (measure(candidate.trimEnd(), font) <= width) {
        line = candidate;
        return;
      }
      if (line) {
        lines++;
      }
      line = '';
      let chunk = '';
      for (const char of word) {
        if (chunk && measure((chunk + char).trimEnd(), font) > width) {
          lines++;
          chunk = char;
        } else {
          chunk += char;
        }
      }
      line = chunk;
    });
    lines++;
  });
  return Math.max(lines, 1);
}

export interface Axis {
  sizes: number[];
  offsets: number[];
  count: number;
  fallback: number;
}

function buildAxis(sizes: number[], fallback: number): Axis {
  const offsets = new Array(sizes.length + 1);
  offsets[0] = 0;
  sizes.forEach((size, index) => {
    offsets[index + 1] = offsets[index] + size;
  });
  return { sizes, offsets, count: sizes.length, fallback };
}

export function axisPos(axis: Axis, index: number): number {
  if (index <= axis.count) {
    return axis.offsets[Math.max(index, 0)];
  }
  return axis.offsets[axis.count] + (index - axis.count) * axis.fallback;
}

export function axisSize(axis: Axis, index: number): number {
  return index < axis.count ? axis.sizes[index] : axis.fallback;
}

export function axisIndex(axis: Axis, position: number): number {
  const end = axis.offsets[axis.count];
  if (position >= end) {
    return axis.count + Math.floor((position - end) / axis.fallback);
  }
  let low = 0;
  let high = axis.count - 1;
  while (low < high) {
    const middle = (low + high + 1) >> 1;
    if (axis.offsets[middle] <= position) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  return Math.max(low, 0);
}

export interface TextRunLayout {
  text: string;
  font: FontStyle;
}

export interface TextLayout {
  mode: 'line' | 'block' | 'split';
  content: string;
  runs?: TextRunLayout[];
  font: FontStyle;
  color: string;
  width: number;
  offset: number;
  align: 'left' | 'center' | 'right' | 'justify' | 'distributed';
  valign: 'top' | 'middle' | 'bottom';
  wrap: boolean;
  clipFrom: number;
  clipTo: number;
  indent: number;
  rotation: number;
  fontScale: number;
  lineHeight: number;
  fill?: { before: string; after: string };
}

export interface LayoutCell {
  row: number;
  col: number;
  rowEnd: number;
  colEnd: number;
  minCol: number;
  maxCol: number;
  background?: string;
  erase: boolean;
  borders?: CellBorders;
  text?: TextLayout;
  dataBar?: DataBar;
  icon?: CellIcon;
}

export interface CellRange {
  rowFrom: number;
  rowTo: number;
  colFrom: number;
  colTo: number;
}

export interface SheetLayout {
  sheet: Sheet;
  columns: Axis;
  rows: Axis;
  rowItems: LayoutCell[][];
  mergeItems: LayoutCell[];
  mergeByCell: Map<number, SheetMerge>;
  usedRows: number;
  usedColumns: number;
  extentRows: number;
  extentColumns: number;
  rowHeaderWidth: number;
  digitWidth: number;
}

export interface SizeOverrides {
  columns: Map<number, number | 'auto'>;
  rows: Map<number, number | 'auto'>;
}

const COLUMN_LIMIT = 16384;
const MERGE_CELL_LIMIT = 2_000_000;

function cellKey(row: number, col: number): number {
  return row * COLUMN_LIMIT + col;
}

function pickFont(base: FontStyle, style?: FontStyle): FontStyle {
  if (!style) {
    return base;
  }
  return {
    fontName: style.fontName ?? base.fontName,
    fontSize: style.fontSize ?? base.fontSize,
    bold: style.bold ?? base.bold,
    italic: style.italic ?? base.italic,
    underline: style.underline ?? base.underline,
    strike: style.strike ?? base.strike,
    color: style.color ?? base.color,
    vertAlign: style.vertAlign ?? base.vertAlign,
  };
}

function isBlank(cell?: SheetCell): boolean {
  return !cell || (!cell.text && !cell.icon && !cell.dataBar);
}

function hasBorders(borders?: CellBorders): boolean {
  return Boolean(
    borders &&
    (borders.top || borders.right || borders.bottom || borders.left || borders.diagonalUp || borders.diagonalDown),
  );
}

function textWidth(cell: SheetCell, font: FontStyle, content: string): number {
  if (!cell.runs) {
    return measure(content, font);
  }
  return cell.runs.reduce((sum, run) => sum + measure(run.text.replace(/\r?\n/g, ''), pickFont(font, run.font)), 0);
}

export function buildLayout(sheet: Sheet, overrides?: SizeOverrides): SheetLayout {
  const defaultFont: FontStyle = { fontName: 'Calibri', fontSize: 11, ...sheet.defaultFont };
  const maxDigit = digitWidth(defaultFont);
  const usedRows = sheet.rows.length;
  const usedColumns = sheet.rows.reduce((max, row) => Math.max(max, row.length), 0);

  const defaultColumn =
    sheet.defaultColumnWidth !== undefined
      ? columnCharsToPx(sheet.defaultColumnWidth, maxDigit)
      : defaultColumnPx(sheet.baseColumnWidth, maxDigit);
  const mergeByCell = new Map<number, SheetMerge>();
  let mergeBudget = MERGE_CELL_LIMIT;
  sheet.merges.forEach((merge) => {
    const rowTo = Math.min(merge.rowTo, Math.max(usedRows, merge.rowFrom));
    const colTo = Math.min(merge.colTo, Math.max(usedColumns, merge.colFrom), COLUMN_LIMIT - 1);
    for (let row = merge.rowFrom; row <= rowTo && mergeBudget > 0; row++) {
      for (let col = merge.colFrom; col <= colTo && mergeBudget > 0; col++) {
        mergeByCell.set(cellKey(row, col), merge);
        mergeBudget--;
      }
    }
  });

  const columnOverrides = overrides?.columns ?? new Map<number, number | 'auto'>();
  const rowOverrides = overrides?.rows ?? new Map<number, number | 'auto'>();
  const lastOverride = (map: Map<number, unknown>) => Math.max(-1, ...map.keys()) + 1;

  const autoColumnWidth = (col: number): number => {
    let width = 0;
    sheet.rows.forEach((cells, row) => {
      const cell = cells[col];
      if (isBlank(cell) || mergeByCell.has(cellKey(row, col)) || cell.style?.rotation) {
        return;
      }
      const font = pickFont(defaultFont, cell.style);
      const lines = cell.style?.wrap ? cell.text.split(/\r?\n/) : [cell.text.replace(/\r?\n/g, '')];
      const textWidth = Math.max(...lines.map((line) => measure(line, font)));
      width = Math.max(width, Math.ceil(textWidth + CELL_PADDING * 2 + 1 + (cell.style?.indent ?? 0) * INDENT_STEP));
    });
    return width || defaultColumn;
  };

  const columnCount = Math.max(
    usedColumns,
    sheet.columnWidths.length,
    sheet.hiddenColumns.length,
    lastOverride(columnOverrides),
  );
  const columnSizes: number[] = [];
  for (let col = 0; col < columnCount; col++) {
    const override = columnOverrides.get(col);
    if (override === 'auto') {
      columnSizes.push(autoColumnWidth(col));
    } else if (override !== undefined) {
      columnSizes.push(Math.round(override));
    } else if (sheet.hiddenColumns[col]) {
      columnSizes.push(0);
    } else {
      const width = sheet.columnWidths[col];
      columnSizes.push(width === undefined ? defaultColumn : columnCharsToPx(width, maxDigit));
    }
  }
  const columns = buildAxis(columnSizes, defaultColumn);

  const defaultRow =
    sheet.defaultRowHeight !== undefined ? Math.round((sheet.defaultRowHeight * 4) / 3) : lineHeight(defaultFont);
  const rowCount = Math.max(usedRows, sheet.rowHeights.length, sheet.hiddenRows.length, lastOverride(rowOverrides));
  const rowSizes: number[] = [];
  for (let row = 0; row < rowCount; row++) {
    const override = rowOverrides.get(row);
    if (typeof override === 'number') {
      rowSizes.push(Math.round(override));
      continue;
    }
    if (override !== 'auto' && sheet.hiddenRows[row]) {
      rowSizes.push(0);
      continue;
    }
    const height = sheet.rowHeights[row];
    if (override !== 'auto' && height !== undefined) {
      rowSizes.push(Math.round((height * 4) / 3));
      continue;
    }
    let needed = defaultRow;
    (sheet.rows[row] ?? []).forEach((cell, col) => {
      if (isBlank(cell) || mergeByCell.has(cellKey(row, col))) {
        return;
      }
      const font = pickFont(defaultFont, cell.style);
      const fontLine = cell.runs
        ? Math.max(...cell.runs.map((run) => lineHeight(pickFont(font, run.font))))
        : lineHeight(font);
      let height = fontLine;
      const style = cell.style;
      const rotation = style?.rotation ?? 0;
      if (rotation === 255) {
        height = fontLine * Math.max(cell.text.length, 1);
      } else if (rotation) {
        const angle = ((rotation > 90 ? rotation - 90 : rotation) * Math.PI) / 180;
        height = Math.abs(Math.sin(angle)) * measure(cell.text, font) + Math.abs(Math.cos(angle)) * fontLine;
      } else if (style?.wrap || style?.align === 'justify' || style?.align === 'distributed') {
        const available = axisSize(columns, col) - CELL_PADDING * 2 - (style?.indent ?? 0) * INDENT_STEP;
        height = wrapLines(cell.text, font, available) * fontLine;
      }
      needed = Math.max(needed, Math.ceil(height));
    });
    rowSizes.push(needed);
  }
  const rows = buildAxis(rowSizes, defaultRow);

  const rowItems: LayoutCell[][] = [];
  const mergeItems: LayoutCell[] = [];

  const cellAt = (row: number, col: number) => sheet.rows[row]?.[col];
  const isFree = (row: number, col: number) => isBlank(cellAt(row, col)) && !mergeByCell.has(cellKey(row, col));

  sheet.rows.forEach((cells, row) => {
    const items: LayoutCell[] = [];
    cells.forEach((cell, col) => {
      const merge = mergeByCell.get(cellKey(row, col));
      const isAnchor = merge && merge.rowFrom === row && merge.colFrom === col;
      const style = cell.style;

      if (merge && !isAnchor) {
        const borders = style?.borders;
        if (!hasBorders(borders)) {
          return;
        }
        const visible: CellBorders = {
          top: row === merge.rowFrom ? borders.top : undefined,
          bottom: row === merge.rowTo ? borders.bottom : undefined,
          left: col === merge.colFrom ? borders.left : undefined,
          right: col === merge.colTo ? borders.right : undefined,
        };
        if (!hasBorders(visible)) {
          return;
        }
        items.push({ row, col, rowEnd: row, colEnd: col, minCol: col, maxCol: col, erase: false, borders: visible });
        return;
      }

      const rowEnd = merge ? merge.rowTo : row;
      const colEnd = merge ? merge.colTo : col;
      const background = style?.background;
      let borders = style?.borders;
      if (merge && hasBorders(borders)) {
        borders = {
          top: borders.top,
          left: borders.left,
          bottom: rowEnd === row ? borders.bottom : undefined,
          right: colEnd === col ? borders.right : undefined,
          diagonalUp: borders.diagonalUp,
          diagonalDown: borders.diagonalDown,
        };
      }
      const item: LayoutCell = {
        row,
        col,
        rowEnd,
        colEnd,
        minCol: col,
        maxCol: colEnd,
        background,
        erase: Boolean(merge) && !background,
        borders: hasBorders(borders) ? borders : undefined,
        dataBar: cell.dataBar,
        icon: cell.icon,
      };

      const showText = cell.text && !cell.icon?.hideValue;
      if (showText) {
        item.text = layoutText(cell, row, col, rowEnd, colEnd, Boolean(merge));
      }

      if (item.text?.mode === 'line') {
        item.minCol = Math.min(item.minCol, item.text.clipFrom);
        item.maxCol = Math.max(item.maxCol, item.text.clipTo);
      }
      if (!item.background && !item.borders && !item.text && !item.dataBar && !item.icon && !item.erase) {
        return;
      }
      if (merge) {
        mergeItems.push(item);
      } else {
        items.push(item);
      }
    });
    rowItems[row] = items;
  });

  function layoutText(
    cell: SheetCell,
    row: number,
    col: number,
    rowEnd: number,
    colEnd: number,
    merged: boolean,
  ): TextLayout {
    const style = cell.style ?? {};
    const font = pickFont(defaultFont, style);
    const rotation = style.rotation ?? 0;
    const isNumeric = cell.kind === 'number';
    let align: TextLayout['align'];
    switch (style.align) {
      case 'left':
      case 'right':
      case 'justify':
      case 'distributed':
        align = style.align;
        break;
      case 'center':
      case 'centerContinuous':
        align = 'center';
        break;
      case 'fill':
        align = 'left';
        break;
      default:
        align = isNumeric ? 'right' : cell.kind === 'boolean' || cell.kind === 'error' ? 'center' : 'left';
    }
    const valign: TextLayout['valign'] =
      style.valign === 'middle'
        ? 'middle'
        : style.valign === 'top' || style.valign === 'justify' || style.valign === 'distributed'
          ? 'top'
          : 'bottom';
    const wrap = Boolean(
      style.wrap ||
      style.align === 'justify' ||
      style.align === 'distributed' ||
      style.valign === 'justify' ||
      style.valign === 'distributed',
    );
    const indent = style.indent ?? 0;
    const cellWidth = axisPos(columns, colEnd + 1) - axisPos(columns, col);
    const available = cellWidth - CELL_PADDING * 2 - 1 - indent * INDENT_STEP;
    const runs = cell.runs?.map((run) => ({ text: run.text, font: pickFont(font, run.font) }));
    const color = cell.formatColor ?? style.color ?? defaultFont.color ?? '#000000';

    const base: TextLayout = {
      mode: 'block',
      content: cell.text,
      runs,
      font,
      color,
      width: 0,
      offset: 0,
      align,
      valign,
      wrap,
      clipFrom: col,
      clipTo: colEnd,
      indent,
      rotation,
      fontScale: 1,
      lineHeight: runs ? Math.max(...runs.map((run) => lineHeight(run.font))) : lineHeight(font),
    };

    const cellStart = axisPos(columns, col);
    const cellEnd = axisPos(columns, colEnd + 1);
    const startFor = (width: number) =>
      align === 'right'
        ? cellEnd - CELL_PADDING - 1 - indent * INDENT_STEP - width
        : align === 'center'
          ? (cellStart + cellEnd) / 2 - width / 2
          : cellStart + CELL_PADDING + 1 + indent * INDENT_STEP;
    const hashes = () => '#'.repeat(Math.max(1, Math.floor(available / measure('#', font))));

    if (cell.fill && !wrap && !rotation) {
      if (isNumeric && measure(cell.text, font) > available + 1) {
        const content = hashes();
        const width = measure(content, font);
        return { ...base, mode: 'line', content, runs: undefined, width, offset: startFor(width) - cellStart };
      }
      return { ...base, mode: 'split', fill: cell.fill };
    }

    if (wrap || rotation) {
      return base;
    }

    const singleLine = cell.text.replace(/\r?\n/g, '');
    let content = singleLine;
    let width = textWidth(cell, font, content);

    if (style.shrink && width > available && width > 0) {
      return { ...base, mode: 'block', content, fontScale: Math.max(available / width, 0.1) };
    }

    if (style.align === 'fill') {
      const repeat = Math.max(1, Math.floor(available / Math.max(width, 1)));
      return { ...base, mode: 'block', content: content.repeat(repeat), runs: undefined };
    }

    if (merged) {
      return { ...base, content, runs: runs?.map((run) => ({ ...run, text: run.text.replace(/\r?\n/g, '') })) };
    }

    if (isNumeric) {
      if (width > available + 1) {
        let fitted: string | null = null;
        if (isGeneralFormat(cell.formatCode) && typeof cell.value === 'number') {
          for (let length = 10; length >= 1 && !fitted; length--) {
            const candidate = generalText(cell.value, length);
            if (candidate && measure(candidate, font) <= available + 1) {
              fitted = candidate;
            }
          }
        }
        content = fitted ?? hashes();
        width = measure(content, font);
      }
      return { ...base, mode: 'line', content, runs: undefined, width, offset: startFor(width) - cellStart };
    }

    let clipFrom = col;
    let clipTo = col;
    if (style.align === 'centerContinuous') {
      while (
        clipTo + 1 < COLUMN_LIMIT &&
        isFree(row, clipTo + 1) &&
        cellAt(row, clipTo + 1)?.style?.align === 'centerContinuous'
      ) {
        clipTo++;
      }
      const spanWidth = axisPos(columns, clipTo + 1) - cellStart;
      return {
        ...base,
        mode: 'line',
        content,
        runs: runs?.map((run) => ({ ...run, text: run.text.replace(/\r?\n/g, '') })),
        width,
        offset: (spanWidth - width) / 2,
        clipFrom,
        clipTo,
      };
    }

    const textStart = startFor(width);
    const textEnd = textStart + width;
    if (textEnd > cellEnd && align !== 'right') {
      while (clipTo + 1 < COLUMN_LIMIT && axisPos(columns, clipTo + 1) < textEnd && isFree(row, clipTo + 1)) {
        clipTo++;
      }
    }

    if (textStart < cellStart && align !== 'left') {
      while (clipFrom - 1 >= 0 && axisPos(columns, clipFrom) > textStart && isFree(row, clipFrom - 1)) {
        clipFrom--;
      }
    }

    return {
      ...base,
      mode: 'line',
      content,
      runs: runs?.map((run) => ({ ...run, text: run.text.replace(/\r?\n/g, '') })),
      width,
      offset: textStart - axisPos(columns, clipFrom),
      clipFrom,
      clipTo,
    };
  }

  let extentColumns = usedColumns;
  let extentRows = usedRows;
  rowItems.forEach((items) => items.forEach((item) => (extentColumns = Math.max(extentColumns, item.maxCol + 1))));
  mergeItems.forEach((item) => {
    extentColumns = Math.max(extentColumns, item.maxCol + 1);
    extentRows = Math.max(extentRows, item.rowEnd + 1);
  });
  sheet.images.forEach((image) => {
    const to = image.to ?? image.from;
    const right = image.to
      ? to.col + 1
      : axisIndex(columns, axisPos(columns, image.from.col) + image.from.colOffset + (image.width ?? 0)) + 1;
    const bottom = image.to
      ? to.row + 1
      : axisIndex(rows, axisPos(rows, image.from.row) + image.from.rowOffset + (image.height ?? 0)) + 1;
    extentColumns = Math.max(extentColumns, right);
    extentRows = Math.max(extentRows, bottom);
  });

  const lastRow = Math.max(usedRows, 1);
  const digits = String(lastRow + 200).length;

  return {
    sheet,
    columns,
    rows,
    rowItems,
    mergeItems,
    mergeByCell,
    usedRows,
    usedColumns,
    extentRows,
    extentColumns,
    rowHeaderWidth: Math.max(digits, 2) * 7 + 12,
    digitWidth: maxDigit,
  };
}

export function mergeAt(layout: SheetLayout, row: number, col: number): SheetMerge | undefined {
  return layout.mergeByCell.get(cellKey(row, col));
}

export function columnName(index: number): string {
  let name = '';
  for (let rest = index; rest >= 0; rest = Math.floor(rest / 26) - 1) {
    name = String.fromCharCode(65 + (rest % 26)) + name;
  }
  return name;
}
